import React, { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { fetchFinancialsTimeseries } from "../core/axios/endpoints";

const fmtEuro = (n) =>
  `€ ${(n ?? 0).toLocaleString("nl-NL", {
    maximumFractionDigits: 0,
  })}`;

const fmtCount = (n) => (n ?? 0).toLocaleString("nl-NL");

const monthLabels = {
  "01": "jan",
  "02": "feb",
  "03": "mrt",
  "04": "apr",
  "05": "mei",
  "06": "jun",
  "07": "jul",
  "08": "aug",
  "09": "sep",
  10: "okt",
  11: "nov",
  12: "dec",
};

const monthLabel = (ym) => {
  const [, m] = ym.split("-");
  return monthLabels[m] ?? ym;
};

const RANGE_OPTIONS = [
  { value: "6m", label: "6 mnd" },
  { value: "12m", label: "12 mnd" },
  { value: "24m", label: "24 mnd" },
];

const Card = ({ title, subtitle, action, children, padded = true }) => (
  <div className="rounded bg-white shadow-card">
    <div className="flex items-start justify-between gap-3 border-b border-line px-5 py-3">
      <div>
        <h3 className="text-base font-semibold text-ink">{title}</h3>
        {subtitle ? (
          <p className="mt-0.5 text-xs text-ink-subtlest">{subtitle}</p>
        ) : null}
      </div>
      {action ?? null}
    </div>
    <div className={padded ? "p-4" : ""}>{children}</div>
  </div>
);

const FinancialCharts = () => {
  const [range, setRange] = useState("12m");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchFinancialsTimeseries(range)
      .then((res) => {
        if (cancelled) return;
        setData(res?.data ?? res);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.error ?? err?.message ?? "Kon data niet laden");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [range]);

  const revenueData = useMemo(
    () =>
      (data?.revenue ?? []).map((r) => ({
        ...r,
        label: monthLabel(r.month),
      })),
    [data],
  );

  const volumeData = useMemo(
    () =>
      (data?.volume ?? []).map((v) => ({
        ...v,
        label: monthLabel(v.month),
      })),
    [data],
  );

  const totalRevenue = useMemo(
    () => revenueData.reduce((s, r) => s + (r.total ?? 0), 0),
    [revenueData],
  );

  const totalVolume = useMemo(
    () =>
      volumeData.reduce(
        (s, v) =>
          s + (v.eia ?? 0) + (v.eiaZon ?? 0) + (v.isde ?? 0) + (v.btwCustomer ?? 0) + (v.btwPartner ?? 0),
        0,
      ),
    [volumeData],
  );

  const funnel = data?.funnel ?? { total: 0, qualified: 0, paid: 0 };
  const aging = data?.aging ?? { notYetDue: 0, d0_14: 0, d15_30: 0, d30plus: 0 };

  const fmtSel = (
    <div className="inline-flex rounded bg-[#091E420F] p-0.5">
      {RANGE_OPTIONS.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => setRange(opt.value)}
          className={`rounded px-2.5 py-1 text-xs font-medium transition ${
            range === opt.value
              ? "bg-white text-brand shadow-card"
              : "text-ink-subtle hover:text-ink"
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );

  if (error) {
    return (
      <div className="rounded bg-[#FFECEB] px-3 py-2 text-sm text-[#AE2A19]">
        Charts niet geladen: {error}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-base font-semibold text-ink">
            Financiële inzichten
          </h2>
          <p className="text-xs text-ink-subtlest">
            {loading ? "Laden…" : `Periode: laatste ${range.replace("m", " maanden")}`}
          </p>
        </div>
        {fmtSel}
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card title="Totale omzet (periode)" subtitle="Subsidie + BTW + partner">
          <p className="text-2xl font-medium tabular-nums text-ink">
            {fmtEuro(totalRevenue)}
          </p>
        </Card>
        <Card title="Aanvragen (periode)" subtitle="Subsidie + BTW">
          <p className="text-2xl font-medium tabular-nums text-ink">
            {fmtCount(totalVolume)}
          </p>
        </Card>
        <Card title="BTW funnel (alltime)" subtitle="ingestuurd → geschikt → betaald">
          <div className="flex items-end gap-1 text-xs tabular-nums">
            <span className="text-ink">
              <span className="text-lg font-medium">{fmtCount(funnel.total)}</span> totaal
            </span>
            <span className="text-ink-subtlest">›</span>
            <span className="text-brand">
              <span className="font-semibold">{fmtCount(funnel.qualified)}</span> geschikt
            </span>
            <span className="text-ink-subtlest">›</span>
            <span className="text-brand">
              <span className="font-semibold">{fmtCount(funnel.paid)}</span> betaald
            </span>
          </div>
        </Card>
      </div>

      <Card
        title="Omzet per maand"
        subtitle="Subsidie-facturen, BTW klant en partner-facturen gestapeld"
      >
        <div className="h-72 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={revenueData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#DCDFE4" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#626F86" }} axisLine={false} tickLine={false} />
              <YAxis
                tick={{ fontSize: 11, fill: "#626F86" }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => `€${Math.round(v / 1000)}k`}
              />
              <Tooltip
                contentStyle={{ fontSize: 12, borderRadius: 3, borderColor: "#DCDFE4" }}
                formatter={(value, name) => [
                  fmtEuro(value),
                  name === "subsidie"
                    ? "Subsidie-facturen"
                    : name === "btwCustomer"
                    ? "BTW klant"
                    : name === "partner"
                    ? "Partner-facturen"
                    : name,
                ]}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Area
                type="monotone"
                dataKey="subsidie"
                stackId="1"
                stroke="#0C66E4"
                fill="#0C66E4"
                fillOpacity={0.35}
                name="Subsidie"
              />
              <Area
                type="monotone"
                dataKey="btwCustomer"
                stackId="1"
                stroke="#09326C"
                fill="#09326C"
                fillOpacity={0.35}
                name="BTW klant"
              />
              <Area
                type="monotone"
                dataKey="partner"
                stackId="1"
                stroke="#5E4DB2"
                fill="#5E4DB2"
                fillOpacity={0.35}
                name="Partner"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card
        title="Aanvragen-volume per maand"
        subtitle="Per regeling gestapeld"
      >
        <div className="h-72 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={volumeData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#DCDFE4" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#626F86" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#626F86" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 3, borderColor: "#DCDFE4" }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="eia" stackId="a" fill="#0C66E4" name="EIA" />
              <Bar dataKey="eiaZon" stackId="a" fill="#09326C" name="EIA Zon" />
              <Bar dataKey="isde" stackId="a" fill="#44546F" name="ISDE" />
              <Bar dataKey="btwCustomer" stackId="a" fill="#8590A2" name="BTW klant" />
              <Bar dataKey="btwPartner" stackId="a" fill="#5E4DB2" name="BTW partner" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Partner-facturen aging" subtitle="Open op factuur (verstuurd of overdue)">
          <ul className="space-y-2 text-sm">
            {[
              { label: "Nog niet vervallen", value: aging.notYetDue, tone: "bg-ink-disabled" },
              { label: "0 - 14 dagen", value: aging.d0_14, tone: "bg-brand" },
              { label: "15 - 30 dagen", value: aging.d15_30, tone: "bg-[#7F5F01]" },
              { label: "30+ dagen", value: aging.d30plus, tone: "bg-[#C9372C]" },
            ].map((b) => {
              const max =
                Math.max(
                  aging.notYetDue,
                  aging.d0_14,
                  aging.d15_30,
                  aging.d30plus,
                  1,
                );
              const pct = Math.max(2, Math.round((b.value / max) * 100));
              return (
                <li key={b.label}>
                  <div className="mb-1 flex items-baseline justify-between text-xs">
                    <span className="text-ink-subtle">{b.label}</span>
                    <span className="font-medium tabular-nums text-ink">
                      {fmtEuro(b.value)}
                    </span>
                  </div>
                  <div className="h-1.5 w-full overflow-hidden rounded bg-[#091E420F]">
                    <div className={`h-full ${b.tone}`} style={{ width: `${pct}%` }} />
                  </div>
                </li>
              );
            })}
          </ul>
        </Card>

        <Card title="BTW kwalificatie-mix" subtitle="Verdeling van alle BTW aanvragen">
          <ul className="space-y-2 text-sm">
            {[
              { label: "Geschikt", value: funnel.qualified, tone: "bg-brand" },
              { label: "Bel klant", value: funnel.callRequired, tone: "bg-[#7F5F01]" },
              { label: "Handmatige beoordeling", value: funnel.disqualified, tone: "bg-[#7F5F01]" },
            ].map((b) => {
              const pct =
                funnel.total > 0
                  ? Math.max(2, Math.round((b.value / funnel.total) * 100))
                  : 0;
              return (
                <li key={b.label}>
                  <div className="mb-1 flex items-baseline justify-between text-xs">
                    <span className="text-ink-subtle">{b.label}</span>
                    <span className="font-medium tabular-nums text-ink">
                      {fmtCount(b.value)} ({pct}%)
                    </span>
                  </div>
                  <div className="h-1.5 w-full overflow-hidden rounded bg-[#091E420F]">
                    <div className={`h-full ${b.tone}`} style={{ width: `${pct}%` }} />
                  </div>
                </li>
              );
            })}
          </ul>
        </Card>
      </div>
    </div>
  );
};

export default FinancialCharts;
